/**
 * Required Fields API.
 *
 * Reads and writes the per-category required-field configuration stored in the
 * `support/internal/required_field` table. Each record holds a JSON-encoded list
 * of field keys that are required for a given entity (customer, driver, etc.).
 * This module parses that blob and layers it over the static `MANDATORY_FIELDS`
 * and optional-field catalog from `requiredFieldsMappings` to produce a full
 * `RequiredFieldConfig` for the UI.
 */
import { logger } from "@/shared/lib/logger";
import type { AxiosInstance } from 'axios';
import type { CategoryType } from '@/shared/constants/requiredFieldsMappings';
import type { RequiredFieldConfig } from '../types/requiredFields.types';
import { apiRoutes } from "@/shared/api/routes";
import {
  MANDATORY_FIELDS,
  getOptionalFieldKeys,
  getFieldLabel,
} from '@/shared/constants/requiredFieldsMappings';

/**
 * Raw row shape returned by the `required_field/select_query` endpoint.
 *
 * Tuple layout: `[id, value, entity_name]` where:
 * - `id` — numeric primary key of the record.
 * - `value` — JSON-encoded string; when parsed yields a `string[]` of required field keys.
 * - `entity_name` — the category this config applies to (e.g. `"customer"`, `"driver"`).
 */
type RawRequiredFieldData = [number, string, string];

export interface RequiredFieldsApiResponse {
  data: RawRequiredFieldData[];
  info: {
    limit: number;
    results: number;
    total_results: number;
    elapsed_total: number;
    elapsed_per_item: number;
    db_query: string;
    db_query_parameters: Record<string, unknown>;
    select_query: boolean;
  };
}

export interface UpdateRequiredFieldsResponse {
  result: string;
  success: boolean;
  failure: boolean;
  api_version: number;
  successes: Array<{ resource_id: string }>;
}

/**
 * Creates the Required Fields API
 * Handles all API calls related to required fields configuration
 */
export const createRequiredFieldsApi = (apiClient: AxiosInstance) => ({
  /**
   * Get required fields configuration for a single category.
   *
   * Fetches the stored `value` JSON blob for the category, parses it to a
   * `string[]` of required field keys, and composes it with the static
   * mandatory-field list and the optional-field catalog.
   *
   * Error handling:
   * - If the stored `value` fails to `JSON.parse` (malformed blob), the error is
   *   logged and parsing falls back to an empty array — meaning no optional
   *   fields are marked required, but mandatory fields are still returned.
   * - If the network/API call itself fails, the error is logged and a default
   *   configuration (mandatory fields only, no optional fields marked required)
   *   is returned instead of throwing. Callers receive a usable shape.
   *
   * @param category - The category type (`customer`, `driver`, `location`, `company`, `trip`, `vehicle`).
   * @returns A fully composed `RequiredFieldConfig` for the category.
   */
  getRequiredFields: async (category: CategoryType): Promise<RequiredFieldConfig> => {
    try {
      // Make API call using select_query endpoint
      const response = await apiClient.post<RequiredFieldsApiResponse>(
        apiRoutes.proxy('support/internal/required_field/select_query'),
        {
          select: ['id', 'value', 'entity_name'],
          query: [['in', 'entity_name', [category]]],
        }
      );

      let requiredFieldKeys: string[] = [];

      // Parse the response data
      if (response.data.data && response.data.data.length > 0) {
        const [, valueJson] = response.data.data[0];
        // Value is a JSON string containing an array of field keys
        try {
          requiredFieldKeys = JSON.parse(valueJson);
        } catch (error) {
          logger.error('Failed to parse required fields value:', error);
          requiredFieldKeys = [];
        }
      }

      // Build mandatory fields
      const mandatoryFields = MANDATORY_FIELDS[category].map((key) => ({
        id: `${category}-${key}`,
        fieldKey: key,
        displayLabel: getFieldLabel(category, key),
        category,
        isMandatory: true,
        isRequired: true,
        fieldType: 'text' as const,
      }));

      // Build optional fields
      const optionalFieldKeys = getOptionalFieldKeys(category);
      const optionalFields = optionalFieldKeys.map((key) => ({
        id: `${category}-${key}`,
        fieldKey: key,
        displayLabel: getFieldLabel(category, key),
        category,
        isMandatory: false,
        isRequired: requiredFieldKeys.includes(key),
        fieldType: 'text' as const,
      }));

      return {
        category,
        mandatoryFields,
        optionalFields,
      };
    } catch (error) {
      logger.error(`Failed to fetch required fields for ${category}:`, error);

      // Return default configuration on error
      const mandatoryFields = MANDATORY_FIELDS[category].map((key) => ({
        id: `${category}-${key}`,
        fieldKey: key,
        displayLabel: getFieldLabel(category, key),
        category,
        isMandatory: true,
        isRequired: true,
        fieldType: 'text' as const,
      }));

      const optionalFieldKeys = getOptionalFieldKeys(category);
      const optionalFields = optionalFieldKeys.map((key) => ({
        id: `${category}-${key}`,
        fieldKey: key,
        displayLabel: getFieldLabel(category, key),
        category,
        isMandatory: false,
        isRequired: false,
        fieldType: 'text' as const,
      }));

      return {
        category,
        mandatoryFields,
        optionalFields,
      };
    }
  },

  /**
   * Update required fields configuration for a category
   * @param category - The category type
   * @param requiredFieldKeys - Array of field keys that should be required
   */
  updateRequiredFields: async (
    category: CategoryType,
    requiredFieldKeys: string[]
  ): Promise<{ success: boolean; message: string }> => {
    try {
      // API expects value as a JSON string
      const valueString = JSON.stringify(requiredFieldKeys);

      // Make PUT request to update required fields
      const response = await apiClient.put<UpdateRequiredFieldsResponse>(
        apiRoutes.proxy('support/internal/required_field'),
        {
          entity_name: category,
          value: valueString,
        }
      );

      if (response.data.success) {
        return {
          success: true,
          message: 'Required fields updated successfully',
        };
      } else {
        return {
          success: false,
          message: 'Failed to update required fields',
        };
      }
    } catch (error) {
      logger.error(`Failed to update required fields for ${category}:`, error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      throw new Error(`Failed to update required fields: ${errorMessage}`);
    }
  },

  /**
   * Get required fields for all categories
   * Useful for batch operations or overview pages
   */
  getAllRequiredFields: async (): Promise<Record<CategoryType, RequiredFieldConfig>> => {
    const categories: CategoryType[] = ['customer', 'driver', 'location', 'company', 'trip', 'vehicle'];

    try {
      // Fetch all required fields in one API call
      const response = await apiClient.post<RequiredFieldsApiResponse>(
        apiRoutes.proxy('support/internal/required_field/select_query'),
        {
          select: ['id', 'value', 'entity_name'],
          query: [['in', 'entity_name', categories]],
        }
      );

      // Parse response and build configs
      const configMap: Partial<Record<CategoryType, string[]>> = {};

      if (response.data.data) {
        response.data.data.forEach(([, valueJson, entityName]) => {
          try {
            const requiredFields = JSON.parse(valueJson);
            configMap[entityName as CategoryType] = requiredFields;
          } catch (error) {
            logger.error(`Failed to parse required fields for ${entityName}:`, error);
            configMap[entityName as CategoryType] = [];
          }
        });
      }

      // Build configs for all categories
      const result: Record<CategoryType, RequiredFieldConfig> = {} as Record<CategoryType, RequiredFieldConfig>;

      categories.forEach((category) => {
        const requiredFieldKeys = configMap[category] || [];

        const mandatoryFields = MANDATORY_FIELDS[category].map((key) => ({
          id: `${category}-${key}`,
          fieldKey: key,
          displayLabel: getFieldLabel(category, key),
          category,
          isMandatory: true,
          isRequired: true,
          fieldType: 'text' as const,
        }));

        const optionalFieldKeys = getOptionalFieldKeys(category);
        const optionalFields = optionalFieldKeys.map((key) => ({
          id: `${category}-${key}`,
          fieldKey: key,
          displayLabel: getFieldLabel(category, key),
          category,
          isMandatory: false,
          isRequired: requiredFieldKeys.includes(key),
          fieldType: 'text' as const,
        }));

        result[category] = {
          category,
          mandatoryFields,
          optionalFields,
        };
      });

      return result;
    } catch (error) {
      logger.error('Failed to fetch all required fields:', error);
      throw error;
    }
  },
});